import { Component, OnInit } from '@angular/core';
import { UsersService } from '../../../services/users.service';
import { SystemLockoutService } from '../../../services/system-lockout.service';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../../models/User';
import { SystemLockout, SystemLockoutCreate, SystemLockoutUpdate } from '../../../models/SystemLockout';

interface SystemLock {
  id: number;
  nome?: string;
  dataHoraInicio: string;
  dataHoraFim: string;
  motivo: string;
  aprovadores: string;
  aprovacao: string;
  status: string;
  aprovador1Aprovado: boolean;
  aprovador2Aprovado: boolean;
  usuarioNome?: string;
  tipo?: string;
  aprovador1Nome?: string;
  aprovador2Nome?: string;
  userIdApproval01: number;
  userIdApproval02: number;
}

@Component({
  selector: 'app-system-lock',
  templateUrl: './system-lock.component.html',
  styleUrls: ['./system-lock.component.css']
})
export class SystemLockComponent implements OnInit {
  // Configuração da tabela
  tableTitle = 'Bloqueios do Sistema';
  showSearch = true;
  searchPlaceholder = 'Buscar bloqueio...';
  clickable = true;

  // Colunas
  columns = [
    { 
      titulo: 'Título', 
      label: 'nome'
    },
    { 
      titulo: 'Data/Hora Início', 
      label: 'dataHoraInicio',
      formatter: (value: any) => this.formatDate(value)
    },
    { 
      titulo: 'Data/Hora Fim', 
      label: 'dataHoraFim',
      formatter: (value: any) => this.formatDate(value)
    },
    { 
      titulo: 'Aprovadores', 
      label: 'aprovadores',
      useInnerHTML: true,
      formatter: (value: any) => {
        return value;
      }
    },
    {
      titulo: 'Aprovação',
      label: 'aprovacao',
      useInnerHTML: true,
      formatter: (value: any) => value
    },
    {
      titulo: 'Status',
      label: 'status',
      useInnerHTML: true,
      formatter: (value: any) => this.buildStatusBadge(value)
    }
  ];

  // Ações
  actions = [
    {
      label: 'Aprovar',
      icon: 'fas fa-check',
      action: 'approve',
      class: 'btn-success'
    },
    {
      label: 'Recusar',
      icon: 'fas fa-times',
      action: 'reject',
      class: 'btn-warning'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  // Dados
  systemLocks: SystemLock[] = [];
  isCreating = false;
  showApproversTable = false;
  users: User[] = [];
  selectedApprovers: User[] = [];
  isLoading = false;

  newLockForm = {
    nome: '',
    dataHoraInicio: '',
    dataHoraFim: '',
    motivo: ''
  };

  approverColumns = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Email', label: 'email' }
  ];

  constructor(
    private usersService: UsersService,
    private systemLockoutService: SystemLockoutService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadSystemLocks();
  }

  loadSystemLocks(): void {
    this.isLoading = true;
    this.systemLockoutService.getAll({ page: 1, pageSize: 100 }).subscribe({
      next: (response) => {
        this.systemLocks = response.data.map(lockout => this.mapSystemLockoutToSystemLock(lockout));
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar bloqueios:', err);
        this.systemLocks = [];
        this.isLoading = false;
      }
    });
  }

  private mapSystemLockoutToSystemLock(lockout: SystemLockout): SystemLock {
    const profile1Name = lockout.UserApproval01?.Name || `Usuário ${lockout.UserIdApproval01}`;
    const profile2Name = lockout.UserApproval02?.Name || `Usuário ${lockout.UserIdApproval02}`;

    return {
      id: lockout.Id,
      nome: lockout.Name,
      dataHoraInicio: lockout.StartDateTime,
      dataHoraFim: lockout.EndDateTime,
      motivo: lockout.Name,
      aprovadores: this.buildApproverAvatars([this.getInitials(profile1Name), this.getInitials(profile2Name)]),
      aprovacao: this.buildApprovalHtml(lockout.Authorized01, lockout.Authorized02),
      status: this.mapStatusFromAPI(lockout.Status),
      aprovador1Aprovado: lockout.Authorized01,
      aprovador2Aprovado: lockout.Authorized02,
      usuarioNome: 'Sistema',
      tipo: 'system-lock',
      aprovador1Nome: profile1Name,
      aprovador2Nome: profile2Name,
      userIdApproval01: lockout.UserIdApproval01,
      userIdApproval02: lockout.UserIdApproval02
    };
  }

  private mapStatusFromAPI(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Pending Approval': 'Aguardando Aprovação',
      'Executed': 'Executada',
      'Rejected': 'Rejeitada'
    };
    return statusMap[status] || status;
  }

  onSearch(event: any): void {
    const searchTerm = event?.target?.value || event;
    // Implementar lógica de busca se necessário
  }

  onRowClick(lock: SystemLock): void {
  }

  onActionClick(event: any): void {
    const action = event.action?.action || event.action;
    const item = event.row || event.item;
    
    switch (action) {
      case 'approve':
        this.approve(item);
        break;
      case 'reject':
        this.reject(item);
        break;
      case 'delete':
        this.delete(item);
        break;
    }
  }

  newLock(): void {
    this.isCreating = true;
    this.showApproversTable = false;
    this.selectedApprovers = [];
    this.newLockForm = { nome: '', dataHoraInicio: '', dataHoraFim: '', motivo: '' };
    this.loadUsers();
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.showApproversTable = false;
    this.selectedApprovers = [];
  }

  loadUsers(): void {
    this.usersService.getAll().subscribe({
      next: (users) => { 
        this.users = users;
      },
      error: () => { 
        this.users = [];
      }
    });
  }

  toggleApproversPanel(): void {
    this.showApproversTable = !this.showApproversTable;
  }

  isApproverSelected(user: User): boolean {
    return this.selectedApprovers.some(u => u.id === user.id);
  }

  onApproverRowClick(user: User): void {
    if (this.isApproverSelected(user)) {
      this.selectedApprovers = this.selectedApprovers.filter(u => u.id !== user.id);
      return;
    }
    if (this.selectedApprovers.length >= 2) {
      return;
    }
    this.selectedApprovers = [...this.selectedApprovers, user];
  }

  get isFormValid(): boolean {
    const isBasicValid = !!this.newLockForm.nome &&
           !!this.newLockForm.dataHoraInicio &&
           !!this.newLockForm.dataHoraFim &&
           !!this.newLockForm.motivo &&
           this.selectedApprovers.length === 2;

    if (!isBasicValid) return false;

    const now = new Date();
    const inicio = new Date(this.newLockForm.dataHoraInicio);
    const fim = new Date(this.newLockForm.dataHoraFim);

    // Data/Hora início deve ser maior ou igual a agora
    if (inicio < now) return false;

    // Data/Hora fim deve ser maior que a Data/Hora início
    if (fim <= inicio) return false;

    return true;
  }

  saveNewLock(): void {
    if (!this.isFormValid) { return; }

    const payload: SystemLockoutCreate = {
      name: this.newLockForm.nome,
      startDateTime: new Date(this.newLockForm.dataHoraInicio).toISOString(),
      endDateTime: new Date(this.newLockForm.dataHoraFim).toISOString(),
      userIdApproval01: this.selectedApprovers[0].id!,
      userIdApproval02: this.selectedApprovers[1].id!
    };

    this.systemLockoutService.create(payload).subscribe({
      next: (lockout) => {
        const newItem = this.mapSystemLockoutToSystemLock(lockout);
        this.systemLocks = [newItem, ...this.systemLocks];
        this.isCreating = false;
        this.showApproversTable = false;
        this.selectedApprovers = [];
      },
      error: (err) => {
        console.error('Erro ao criar bloqueio:', err);
      }
    });
  }

  // Helpers
  getInitials(name?: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    const initials = parts.slice(0, 2).map(p => p[0]).join('').toUpperCase();
    return initials || '?';
  }

  private buildApproverAvatars(initials: string[]): string {
    const [a1, a2] = [initials[0] || '?', initials[1] || '?'];
    return `<span class="approver-avatars">
      <span class="circle-avatar">${a1}</span>
      <span class="circle-avatar alt">${a2}</span>
    </span>`;
  }

  private buildApprovalHtml(ap1: boolean, ap2: boolean): string {
    const pill = (approved: boolean) => approved
      ? `<span class="status-pill approved"><i class="fas fa-check"></i> Aprovado</span>`
      : `<span class="status-pill pending"><i class="fas fa-clock"></i> Pendente</span>`;

    return `<span class="approval-status">${pill(ap1)} ${pill(ap2)}</span>`;
  }

  private buildStatusBadge(status: string): string {
    const s = (status || '').toString().trim().toLowerCase();
    let cls = 'default';
    let label = status || '-';

    // Mapear para estilos padronizados
    if (['executada', 'executado', 'concluida', 'concluída', 'completed', 'complete'].includes(s)) {
      cls = 'complete';
      label = 'Complete';
    } else if (['aguardando aprovação', 'pendente', 'pending approval', 'pending'].includes(s)) {
      cls = 'pending';
      label = 'Pending';
    } else if (['em execução', 'em execucao', 'in progress', 'ativo', 'active'].includes(s)) {
      cls = 'in-progress';
      label = 'In Progress';
    } else if (['agendada', 'scheduled'].includes(s)) {
      cls = 'scheduled';
      label = 'Scheduled';
    } else if (['cancelada', 'cancelado', 'cancelled', 'canceled'].includes(s)) {
      cls = 'cancelled';
      label = 'Cancelled';
    }

    return `<span class="status-badge ${cls}">${label}</span>`;
  }

  private getLoggedUserId(): number | null {
    const userIdStr = this.authService.getUserIdFromToken();
    if (!userIdStr) return null;
    const parsed = parseInt(userIdStr, 10);
    return isNaN(parsed) ? null : parsed;
  }

  approve(lock: SystemLock): void {
    const loggedUserId = this.getLoggedUserId();
    if (!loggedUserId) {
      alert('Não foi possível identificar o usuário logado. Faça login novamente.');
      return;
    }

    const isApprover1 = loggedUserId === lock.userIdApproval01;
    const isApprover2 = loggedUserId === lock.userIdApproval02;

    if (!isApprover1 && !isApprover2) {
      alert('Você não é um dos aprovadores deste bloqueio.');
      return;
    }

    if (isApprover1 && !lock.aprovador1Aprovado) {
      this.systemLockoutService.approveFirst(lock.id).subscribe({
        next: () => {
          lock.aprovador1Aprovado = true;
          lock.aprovacao = this.buildApprovalHtml(true, lock.aprovador2Aprovado);
          if (lock.aprovador2Aprovado) {
            lock.status = 'Executada';
          }
        },
        error: (err) => console.error('Erro ao aprovar:', err)
      });
    } else if (isApprover2 && !lock.aprovador2Aprovado) {
      this.systemLockoutService.approveSecond(lock.id).subscribe({
        next: () => {
          lock.aprovador2Aprovado = true;
          lock.aprovacao = this.buildApprovalHtml(true, true);
          lock.status = 'Executada';
        },
        error: (err) => console.error('Erro ao aprovar:', err)
      });
    } else if (isApprover1 && lock.aprovador1Aprovado) {
      alert('Você já realizou sua aprovação neste bloqueio.');
    } else if (isApprover2 && lock.aprovador2Aprovado) {
      alert('Você já realizou sua aprovação neste bloqueio.');
    }
  }

  reject(lock: SystemLock): void {
    const loggedUserId = this.getLoggedUserId();
    if (!loggedUserId) {
      alert('Não foi possível identificar o usuário logado. Faça login novamente.');
      return;
    }

    const isApprover1 = loggedUserId === lock.userIdApproval01;
    const isApprover2 = loggedUserId === lock.userIdApproval02;

    if (!isApprover1 && !isApprover2) {
      alert('Você não é um dos aprovadores deste bloqueio.');
      return;
    }

    if (confirm('Tem certeza que deseja rejeitar este bloqueio?')) {
      this.systemLockoutService.reject(lock.id).subscribe({
        next: () => {
          lock.status = 'Rejeitada';
          this.loadSystemLocks();
        },
        error: (err) => console.error('Erro ao rejeitar:', err)
      });
    }
  }

  delete(lock: SystemLock): void {
    const loggedUserId = this.getLoggedUserId();
    if (!loggedUserId) {
      alert('Não foi possível identificar o usuário logado. Faça login novamente.');
      return;
    }

    const isApprover1 = loggedUserId === lock.userIdApproval01;
    const isApprover2 = loggedUserId === lock.userIdApproval02;

    if (!isApprover1 && !isApprover2) {
      alert('Você não é um dos aprovadores deste bloqueio. Apenas aprovadores podem excluir.');
      return;
    }

    if (confirm('Tem certeza que deseja excluir este bloqueio?')) {
      this.systemLockoutService.delete(lock.id).subscribe({
        next: () => {
          this.systemLocks = this.systemLocks.filter(l => l.id !== lock.id);
        },
        error: (err) => console.error('Erro ao excluir:', err)
      });
    }
  }

  // Métodos para a tabela customizada
  formatDate(dateStr: string): string {
    if (!dateStr) return '-';
    // Se já estiver formatado (DD/MM/YYYY HH:mm), retorna
    if (dateStr.includes('/')) {
      return dateStr;
    }
    // Formatar datetime-local para formato brasileiro
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  }

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('executada') || s.includes('complete')) return 'executada';
    if (s.includes('aguardando') || s.includes('pending')) return 'aguardando';
    if (s.includes('cancelada') || s.includes('cancelled')) return 'cancelada';
    return 'aguardando';
  }

  getStatusLabel(status: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('executada') || s.includes('complete')) return 'Executada';
    if (s.includes('aguardando') || s.includes('pending')) return 'Aguardando Aprovação';
    if (s.includes('cancelada') || s.includes('cancelled')) return 'Cancelada';
    return status;
  }

  removeApprover(approver: User): void {
    this.selectedApprovers = this.selectedApprovers.filter(a => a.id !== approver.id);
  }

  getLockTypeClass(lock: SystemLock): string {
    const tipo = (lock.tipo || 'system-lock').toLowerCase();
    if (tipo.includes('annual') || tipo.includes('férias')) return 'annual-leave';
    if (tipo.includes('remote') || tipo.includes('remoto')) return 'remote';
    return 'system-lock';
  }

  getLockTypeIcon(lock: SystemLock): string {
    const tipo = (lock.tipo || 'system-lock').toLowerCase();
    if (tipo.includes('annual') || tipo.includes('férias')) return 'fas fa-plane';
    if (tipo.includes('remote') || tipo.includes('remoto')) return 'fas fa-home';
    return 'fas fa-lock';
  }

  getLockTypeLabel(lock: SystemLock): string {
    const tipo = (lock.tipo || 'system-lock').toLowerCase();
    if (tipo.includes('annual') || tipo.includes('férias')) return 'Annual leave';
    if (tipo.includes('remote') || tipo.includes('remoto')) return 'Remote';
    return 'System Lock';
  }

  getRandomGradient(): string {
    return '#3A4D56';
  }

  get minDateTime(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
}
